"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SinkContainer = void 0;
const services_1 = require("./services");
const SinkBuilder_1 = require("./SinkBuilder");
const utils_1 = require("./utils");
const defaultSinkConfig = {
    middlewares: [],
    reducers: {},
    useEffectTrace: false,
    useTrigger: false,
};
class SinkContainer {
    constructor() {
        this.reducers = {};
        this.sinks = {};
        this.getSink = this.getSink.bind(this);
        this.getStore = this.getStore.bind(this);
        this.triggerService = new services_1.TriggerService();
        this.effectService = new services_1.EffectService();
    }
    /**
     * Create store by sink configuration
     * @param {SinkConfiguration} config
     */
    createStore(config) {
        // get config in used by combine default and user config
        const useConfig = Object.assign({}, defaultSinkConfig, config);
        const middlewares = [];
        // configure trigger service
        if (useConfig.useTrigger) {
            middlewares.push(utils_1.createServiceMiddleware(this.triggerService));
        }
        // configure effect service
        this.effectService.enableTrace = useConfig.useEffectTrace;
        middlewares.push(utils_1.createServiceMiddleware(this.effectService));
        // create store, combine reducers with container reducer
        this.store = utils_1.configureStore({
            devToolOptions: useConfig.devToolOptions,
            middlewares: middlewares.concat(useConfig.middlewares),
            preloadedState: useConfig.preloadedState,
            reducers: Object.assign({}, this.reducers, useConfig.reducers),
        });
        const state = this.store.getState() || {};
        // update sink state from preloaded state
        Object.keys(this.sinks).forEach(key => {
            const sinkState = state[key];
            if (sinkState !== undefined) {
                this.sinks[key].setState(sinkState);
            }
        });
        // rebuild reducer combine with sink reducers
        this.rebuildReducer(this.store);
        return this.store;
    }
    /**
     * invoke triggers by action
     * @param action sink action
     */
    invokeTrigger(action) {
        return this.triggerService.invoke(action).value;
    }
    /**
     * invoke effect by action
     * @param action sink action
     */
    invokeEffect(action) {
        return this.effectService.invoke(action).value;
    }
    /**
     * Get traced effect tasks
     * @returns {[Promise]} promise array
     */
    getEffectTasks() {
        return this.effectService.tasks;
    }
    /**
     * Get underlying redux store
     */
    getStore() {
        return this.store;
    }
    /**
     * Get sink instance by sink class
     * @param sink Sink class
     * @returns Sink instance
     */
    getSink(sink) {
        return this.getSinkPrototype(sink).instance;
    }
    /**
     * Get sink prototype by sink class, will build sink if its not built yet in this container
     * @param sink Sink class
     * @returns Sink prototype, contains sink builder and sink configurations
     */
    getSinkPrototype(sink) {
        if (!sink || !sink.prototype) {
            throw new Error(`sink not found`);
        }
        const builder = SinkBuilder_1.SinkBuilder.get(sink.prototype);
        if (!builder.namespace) {
            throw new Error(`please use @sink decorator: ${sink.toString()}`);
        }
        if (!this.sinks[builder.namespace]) {
            this.addSink(builder);
        }
        return this.sinks[builder.namespace];
    }
    addSink(builder) {
        // build sink with build params
        const sink = builder.buildSink({
            getSink: this.getSink,
            getStore: this.getStore
        });
        // add sink to container
        this.sinks[builder.namespace] = sink;
        // create reducer if we got reducers
        const reducerKeys = Object.keys(sink.reducers);
        if (reducerKeys.length > 0) {
            if (this.store) {
                const storeState = this.store.getState();
                const preloadedState = storeState && storeState[sink.namespace];
                if (preloadedState !== undefined) {
                    sink.setState(preloadedState);
                }
            }
            // update reducers in container
            if (sink.reducer) {
                this.reducers[sink.namespace] = sink.reducer;
            }
            // if store is already set, rebuild reducer
            if (this.store) {
                this.rebuildReducer(this.store);
            }
        }
        // register effects
        Object.keys(sink.effects).forEach(key => this.effectService.addEffect(sink.actions[key], sink.effects[key]));
        // register subscribe
        sink.triggers.forEach(trigger => {
            this.triggerService.addTrigger(trigger.actionType, trigger.handler, trigger.options);
        });
    }
    rebuildReducer(store) {
        store.replaceReducer(utils_1.combineReducers(this.reducers));
    }
}
exports.SinkContainer = SinkContainer;
